/* THE PLAN — the screen drawn to scale, dimensioned like a shop drawing.

   Calibrate had no picture of what you were building. Four fields said 1920,
   1080, 4.00 and 2.25, and the SHAPE those describe was something you held in
   your head. This draws it: a rectangle in the real aspect with its pixel count
   on one edge and its metres on the other, and for a corner screen a second
   rectangle sharing the red edge plus a small view from above.

   Two sizes, one routine. Full size it goes on the stage beside the photograph
   (the Plan tab); bare it is a thumbnail at the top of the Screen panel, so the
   shape stays in view while you type numbers at it and switching tabs is
   optional rather than the only way to see what you are making.

   No THREE, no renderer, no S mutation — it reads state and writes SVG, which
   is why it can be pointed at two different elements without either knowing
   about the other. */
import { freeBounds } from '../scene/panels.js';
import { S } from '../state/store.js';

const NS = 'http://www.w3.org/2000/svg';

function mk(tag, attrs, text){
  const n = document.createElementNS(NS, tag);
  for(const k in attrs) n.setAttribute(k, attrs[k]);
  if(text != null) n.textContent = text;
  return n;
}
/* Read off the stylesheet rather than hardcoded, so the drawing follows the
   theme and stays in step with the handles and the hinge it is describing. */
const tok = v => getComputedStyle(document.documentElement).getPropertyValue(v).trim();
const fmt = (v, d) => (+v).toFixed(d);

/* A dimension line: a rule with end ticks and a label off it, the way a drawing
   calls out a length. Horizontal or vertical; nothing else is needed here. */
/* One unit is one pixel (see the viewBox note in drawPlan), so this is a real
   type size rather than a number that changes meaning with the box. */
const FS = 11;
function dim(g, x1, y1, x2, y2, label, side, col, mono){
  const t = 3;
  g.appendChild(mk('line', { x1, y1, x2, y2, stroke:col, 'stroke-width':1 }));
  if(y1 === y2){
    g.appendChild(mk('line', { x1, y1:y1-t, x2:x1, y2:y1+t, stroke:col, 'stroke-width':1 }));
    g.appendChild(mk('line', { x1:x2, y1:y1-t, x2, y2:y1+t, stroke:col, 'stroke-width':1 }));
    g.appendChild(mk('text', { x:(x1+x2)/2, y:y1 + (side === 'above' ? -6 : 13), fill:col,
      'text-anchor':'middle', 'font-size':FS, 'font-family':mono }, label));
  }else{
    g.appendChild(mk('line', { x1:x1-t, y1, x2:x1+t, y2:y1, stroke:col, 'stroke-width':1 }));
    g.appendChild(mk('line', { x1:x1-t, y1:y2, x2:x1+t, y2, stroke:col, 'stroke-width':1 }));
    g.appendChild(mk('text', { x:x1 + (side === 'left' ? -7 : 7), y:(y1+y2)/2 + 3, fill:col,
      'text-anchor':side === 'left' ? 'end' : 'start',
      'font-size':FS, 'font-family':mono }, label));
  }
}

/** Draw the current screen into `svg`. `bare` drops the dimension text for the
 *  thumbnail, where it would be too small to read and the shape is the point. */
export function drawPlan(svg, bare){
  if(!svg) return;
  svg.textContent = '';
  /* The viewBox is set to the element's REAL pixel size, not left fixed. A
     fixed box is scaled to fit by the browser, which scales the labels with it
     — so widening the stage blew "1920 px" up to headline size. Matching the
     box to the element makes one unit one pixel, and the type stays type
     however big the drawing gets. */
  const cw = Math.round(svg.clientWidth), chh = Math.round(svg.clientHeight);
  if(cw > 40 && chh > 30) svg.setAttribute('viewBox', `0 0 ${cw} ${chh}`);
  const vb = (svg.getAttribute('viewBox') || '0 0 440 280').split(/[\s,]+/).map(Number);
  const W = vb[2], H = vb[3];
  /* The left margin has to clear the dimension TEXT, not just the dimension
     line: "1080 px" is set anchored at its right end, so it runs leftwards out
     of the viewBox and gets clipped by it. Measured against the longest label
     the drawing can carry. */
  /* Proportional, with a floor. Fixed margins were tuned against a 440-unit
     box; on a stage three times that the drawing ran edge to edge with its
     dimension lines crammed against the sides. The floor is what keeps the
     longest label ("1080 px", set anchored at its right end and running
     leftwards out of the box) from being clipped at small sizes. */
  /* An array carries two more rows of dimensions than a single screen — the
     margin above, one screen's metres below the wall's — so it needs the room
     for them or they are drawn outside the viewBox and simply clipped. */
  /* Asymmetric on purpose: the array adds ONE row above (the margin) and TWO
     below (one screen's metres, then the wall's overall), so reserving the same
     slack at both ends either clipped the bottom or wasted the top. */
  const freeMode = S.screenArray?.mode === 'free';
  const arrDims  = !bare && !!S.screenArray && !freeMode;
  /* A free layout carries none of the grid's extra rows — no one margin to
     call out above, no one screen's metres to call out below, since screens
     differ — only the same single caption line a grid gets at the very
     bottom. So it wants the ordinary single-screen padding, not the grid's
     wider one. */
  const topExtra = arrDims ? 18 : 0;
  const botExtra = arrDims ? 30 : (freeMode && !bare ? 18 : 0);
  const padX   = bare ? 6 : Math.max(84, W * 0.10);
  const padTop = bare ? 6 : Math.max(34, H * 0.11) + topExtra;
  const padBot = bare ? 6 : Math.max(48, H * 0.15) + botExtra;
  const availW = W - padX*2, availH = H - padTop - padBot;

  const c = S.cornerPanel;
  const pitch = S.res.w / Math.max(1e-6, S.screen.w);      // px per metre, panel one
  const w2m = c ? c.w : 0;
  const k = Math.min(availW / Math.max(1e-6, S.screen.w + w2m),
                     availH / Math.max(1e-6, S.screen.h));
  const w1 = S.screen.w * k, h = S.screen.h * k, w2 = w2m * k;
  const x0 = (W - (w1 + w2)) / 2, y0 = padTop + (availH - h) / 2;

  const face = tok('--accent-tint') || 'rgba(13,153,255,.18)';
  const edge = '#F2C300';                                  // the panel outline, as on the photo
  const hinge = tok('--handle') || '#FF4438';
  const ink = tok('--muted-dim') || '#82898F';
  const mono = tok('--mono') || 'monospace';

  const g = mk('g', {});
  const arr = S.screenArray;
  /* THE WALL'S EXTENT, not its surface. On a single screen the rectangle IS
     the screen and carries the tint; on an array it is the outline the
     dimensions are measured against, and filling it would paint the margins
     the same colour as the displays — which is the one thing this drawing
     exists to tell apart. The screens fill themselves, below. */
  g.appendChild(mk('rect', { x:x0, y:y0, width:w1, height:h,
    fill: arr ? 'none' : face, stroke:edge, 'stroke-width':2 }));
  /* AN ARRAY's individual screens, drawn INSIDE the wall rather than instead of
     it. The wall is what the outer rectangle already is — S.screen holds it, so
     the dimensions either side of this drawing are the wall's and stay right —
     and what the grid adds is where the seams fall across it. Which is the one
     thing the numbers alone cannot show: "3.67 x 1.38 m" and "3 across, 2 down"
     are both readable and still leave you holding the shape in your head.
     Each one carries the tint, so the gaps between them are the drawing's own
     ground showing through — a margin is the absence of screen, and now it
     looks like one. */
  if(freeMode){
    /* Placed straight from each screen's own x/y/w/h, translated through the
       SAME bounding box panelDescs() (scene/panels.js) centres every screen
       in — so this drawing cannot show a layout that disagrees with what is
       actually rendered. Always stroked, unlike the grid: whether any given
       PAIR of free screens touches is a per-pair question a global on/off
       rule cannot answer, so the honest default is to always draw the edge
       and let a real gap show as the ground between two edges rather than as
       the absence of a line. */
    const b = freeBounds(arr.screens);
    for(const s of arr.screens){
      const sx = x0 + (s.x - b.x0) * k, sy = y0 + (s.y - b.y0) * k;
      const sw = s.w * k, sh = s.h * k;
      g.appendChild(mk('rect', { x: sx, y: sy, width: sw, height: sh,
        fill: face, stroke: edge, 'stroke-width': 1, 'stroke-opacity': .7 }));
      /* The screen's own label, centred, so the drawing answers "which
         rectangle is B" without needing a dimension line for each one — which
         an irregular layout has no clean edge to anchor against anyway. Only
         where there is visibly room for it; a screen a few units wide at
         thumbnail scale would show a clipped word instead of a shape. */
      if(!bare && sw > 22 && sh > 14)
        g.appendChild(mk('text', { x: sx + sw/2, y: sy + sh/2 + 4, fill: ink,
          'text-anchor':'middle', 'font-size':FS-1, 'font-family':mono }, s.key));
    }
  }else if(arr){
    /* The seam is drawn ONLY for a wall with no margin at all. An outline is
       centred on the edge it traces, so a stroke from each side meets in the
       middle of a narrow gap and paints over the very thing it was added to
       reveal — and narrow is the normal case, since the rail's thumbnail puts
       a 90 mm margin at under two units. Where there is a margin the ground
       between the screens is the seam; edge to edge, a line is the only thing
       that can show one. */
    const seam = !(arr.gap > 0);
    for(let r = 0; r < arr.rows; r++)
      for(let cc = 0; cc < arr.cols; cc++)
        g.appendChild(mk('rect', {
          x: x0 + cc * (arr.tile.w + arr.gap) * k,
          y: y0 + r  * (arr.tile.h + arr.gap) * k,
          width: arr.tile.w * k, height: arr.tile.h * k,
          /* stroke-opacity, not opacity: the element's own opacity would fade
             the fill with it and put the tint back to somewhere between a
             screen and a gap. */
          fill: face, stroke: seam ? edge : 'none',
          'stroke-width': 1, 'stroke-opacity': .55 }));
  }
  if(c){
    g.appendChild(mk('rect', { x:x0+w1, y:y0, width:w2, height:h,
      fill:face, stroke:edge, 'stroke-width':2, opacity:.92 }));
    // the shared edge, in the same red as the hinge line and its two crosshairs
    g.appendChild(mk('line', { x1:x0+w1, y1:y0, x2:x0+w1, y2:y0+h,
      stroke:hinge, 'stroke-width':bare ? 2 : 3 }));
  }

  if(!bare && freeMode){
    /* NO ONE PIXEL COUNT TO CALL OUT — screens can differ, so the top/left px
       callouts the grid gets (against its one shared tile) have no single
       answer here; each screen's own resolution is what the list editor in
       the LCD Video Wall panel already shows, right beside the number that
       set it, which is a better place for it than a label competing for room
       on an irregular drawing. What the drawing CAN still say without
       inventing a number: the overall box, the same two calls a single
       screen gets. */
    dim(g, x0, y0+h+18, x0+w1, y0+h+18, fmt(S.screen.w,2) + ' m', 'below', ink, mono);
    dim(g, x0+w1+22, y0, x0+w1+22, y0+h, fmt(S.screen.h,2) + ' m', 'right', ink, mono);
    g.appendChild(mk('text', { x:x0, y:y0+h+38, fill:ink,
      'font-size':FS, 'font-family':mono },
      `${arr.screens.length} screens placed freely`));
  }else if(!bare && arr){
    /* AN ARRAY IS DIMENSIONED PER SCREEN, not per wall. S.res across a wall is
       a derived preview number — the wall at the tiles' own pitch, gaps
       included — and nobody builds to it or orders a display in it. What gets
       ordered is one screen's pixels and one screen's metres, so those are what
       the drawing calls out, against the FIRST tile. The wall keeps its overall
       metres, one row further out, because that is the number that has to fit
       the site. */
    const tw = arr.tile.w * k, th = arr.tile.h * k, gp = arr.gap * k;
    dim(g, x0, y0-16, x0+tw, y0-16, arr.res.w + ' px', 'above', ink, mono);
    dim(g, x0-22, y0, x0-22, y0+th, arr.res.h + ' px', 'left', ink, mono);
    dim(g, x0, y0+h+18, x0+tw, y0+h+18, fmt(arr.tile.w,2) + ' m', 'below', ink, mono);
    dim(g, x0, y0+h+38, x0+w1, y0+h+38, fmt(S.screen.w,2) + ' m overall', 'below', ink, mono);
    dim(g, x0+w1+22, y0, x0+w1+22, y0+h, fmt(S.screen.h,2) + ' m', 'right', ink, mono);
    /* The margin, on its own row above the screen dimension rather than beside
       it: the gap is a couple of pixels wide at this scale, so its label is far
       wider than the thing it measures and would sit on top of the neighbouring
       one. Millimetres because that is how a bezel is specified. */
    if(arr.gap > 0 && arr.cols > 1)
      dim(g, x0+tw, y0-34, x0+tw+gp, y0-34,
          Math.round(arr.gap*1000) + ' mm', 'above', ink, mono);
    g.appendChild(mk('text', { x:x0, y:y0+h+68, fill:ink,
      'font-size':FS, 'font-family':mono },
      `${arr.cols} × ${arr.rows} screens · ${arr.cols*arr.rows} files`));
  }else if(!bare){
    dim(g, x0, y0-16, x0+w1, y0-16, S.res.w + ' px', 'above', ink, mono);
    dim(g, x0, y0+h+18, x0+w1, y0+h+18, fmt(S.screen.w,2) + ' m', 'below', ink, mono);
    dim(g, x0-22, y0, x0-22, y0+h, S.res.h + ' px', 'left', ink, mono);
    dim(g, x0+w1+w2+22, y0, x0+w1+w2+22, y0+h, fmt(S.screen.h,2) + ' m', 'right', ink, mono);
    if(c){
      dim(g, x0+w1, y0-16, x0+w1+w2, y0-16, c.resW + ' px', 'above', ink, mono);
      dim(g, x0+w1, y0+h+18, x0+w1+w2, y0+h+18, fmt(c.w,2) + ' m', 'below', ink, mono);
      /* The corner seen from above. Two rectangles side by side say "wide";
         only this says "folded", and the fold is the whole feature. Drawn for
         the sign the solve recovered, so a concave corner reads as one. */
      const a = Math.max(26, Math.min(44, H * 0.09));
      const cx = W - a - 30, cy = H - 26, s = c.convex === false ? -1 : 1;
      g.appendChild(mk('line', { x1:cx-a, y1:cy, x2:cx, y2:cy, stroke:edge,
        'stroke-width':3, 'stroke-linecap':'round' }));
      g.appendChild(mk('line', { x1:cx, y1:cy, x2:cx, y2:cy - s*a, stroke:edge,
        'stroke-width':3, 'stroke-linecap':'round' }));
      g.appendChild(mk('circle', { cx, cy, r:3, fill:hinge }));
      g.appendChild(mk('text', { x:cx-a-6, y:cy+4, fill:ink,
        'text-anchor':'end', 'font-size':FS-1, 'font-family':mono },
        s > 0 ? 'from above' : 'inside corner'));
    }
  }
  svg.appendChild(g);
}

/* THE PHOTOGRAPH'S CEILING, measured rather than declared.

   #calibWrap has to shrink-wrap the photo exactly — the crosshairs are placed
   as percentages of it and #calibSvg is inset:0 of it — so it can never carry a
   height for the image's max-height:100% to resolve against. It was capped with
   a CSS expression instead, which worked only while the stage had a height of
   its own; the stage now takes what the card has left, so the number does not
   exist until layout has run. Read it off the box afterwards.

   Cheap: one getBoundingClientRect and a style write, on the paths that already
   rebuild the rail or swap the view. */
export function fitCalibStage(){
  const st = document.querySelector('.calibstage');
  const img = document.getElementById('calibImg');
  if(!st || !img) return;
  const cs = getComputedStyle(st);
  const h = st.getBoundingClientRect().height
          - parseFloat(cs.paddingTop) - parseFloat(cs.paddingBottom);
  if(h > 40) img.style.maxHeight = Math.floor(h) + 'px';
}

/** Both drawings at once — the stage's and the rail's thumbnail. Safe to call
 *  when either is absent, which is every stage but Calibrate. */
export function syncPlan(){
  fitCalibStage();
  drawPlan(document.getElementById('calibPlan'), false);
  drawPlan(document.getElementById('planMini'), true);
}
